import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { CommonDataParamsService } from './common-data-params.service';
import { EventsService } from '../services/data-api/events.service';
import {
  CollapseOnOption,
  EventTemporality,
  EventFactuality,
  EventsRequestBody,
  EventsEventFilter,
  CollapsedEventsResponse,
} from '../models/events.interface';

export interface EventsContentParams {
  collapse_on?: CollapseOnOption[];
  entity?: string[];
  entityType?: string;
  time?: string;
  timePeriod?: string;
  temporality?: EventTemporality[];
  factuality?: EventFactuality[];
  event_ids?: string[];
  lang?: string[];
  project_id?: string;
  /** Ignore the URL `eventid` filter — the chart matrix needs all events (the clicked one is filtered client-side). */
  ignoreUrlEventIds?: boolean;
}

export interface EventsTimeSeriesResponse {
  events: [string, number][];
  columns: [string, string];
}

export interface EventsTimeSeriesDataPoint {
  date: string;
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class EventsUtilsService {
  // Time out hung responses to the empty fallback. Kept as resilience — the
  // v5 empty-result truncation bug it mitigated was fixed backend-side 2026-07-17.
  static readonly EVENTS_TIMEOUT_MS = 30_000;

  constructor(
    private commonDataParams: CommonDataParamsService,
    private eventsService: EventsService,
  ) {}

  fetchEventsTimeSeries(
    params?: EventsContentParams,
    apiOverrides?: Partial<EventsRequestBody>,
  ): Observable<EventsTimeSeriesDataPoint[]> {
    const body = this.buildEventsRequestBody(
      {
        collapse_on: [
          'event_id',
          'factuality',
          'temporality',
          'role',
          'participant',
        ],
        ...params,
      },
      apiOverrides,
    );

    return this.eventsService.getEventsTimeSeries(body).pipe(
      timeout(EventsUtilsService.EVENTS_TIMEOUT_MS),
      map((response: CollapsedEventsResponse) =>
        this.transformTimeSeriesResponse(response),
      ),
      catchError(() => of([])),
    );
  }

  fetchEventsTimeSeriesMatrix(
    params: EventsContentParams,
    apiOverrides?: Partial<EventsRequestBody>,
  ): Observable<CollapsedEventsResponse> {
    const body = this.buildEventsRequestBody(params, apiOverrides);
    return this.eventsService.getEventsTimeSeries(body).pipe(
      timeout(EventsUtilsService.EVENTS_TIMEOUT_MS),
      catchError(() =>
        of<CollapsedEventsResponse>({ events: [], columns: [] }),
      ),
    );
  }

  // `language_filter` is required by events (unlike score_ts).
  buildEventsRequestBody(
    params: EventsContentParams,
    apiOverrides?: Partial<EventsRequestBody>,
  ): EventsRequestBody {
    const queryParams = this.commonDataParams.getCurrentQueryParams();

    const body: EventsRequestBody = {
      time_range: this.commonDataParams.generateTimeRange({
        type: 'entities',
        time: params.time,
        timePeriod: params.timePeriod,
      }),
    };

    const eventFilter: EventsEventFilter = {};

    const participants = this.commonDataParams.getEventParticipants(
      { entity: params.entity, entityType: params.entityType },
      queryParams,
    );
    if (participants.length > 0) {
      eventFilter.event_participants = participants;
    }

    const eventIds = params.ignoreUrlEventIds
      ? params.event_ids
      : this.commonDataParams.getEventIds(queryParams, params.event_ids);
    if (eventIds?.length) {
      eventFilter.event_ids = eventIds;
    }

    const temporality = this.commonDataParams.getTemporalityWire(
      queryParams.temporality,
      params.temporality,
    );
    if (temporality?.length) {
      eventFilter.temporality = temporality;
    }

    const factuality = this.resolveFactuality(params, queryParams.factuality);
    if (factuality?.length) {
      eventFilter.factuality = factuality;
    }

    if (Object.keys(eventFilter).length > 0) {
      body.event_filter = eventFilter;
    }

    if (params.collapse_on?.length) {
      body.collapse_on = params.collapse_on;
    }

    const lang = this.commonDataParams.getLanguageFilter(
      queryParams,
      params.lang,
    );
    if (lang?.length) {
      body.language_filter = lang;
    }

    const project = params.project_id ?? this.commonDataParams.getProject();
    if (project) {
      body.project = project;
    }

    return { ...body, ...apiOverrides };
  }

  private transformTimeSeriesResponse(
    response: EventsTimeSeriesResponse | CollapsedEventsResponse,
  ): EventsTimeSeriesDataPoint[] {
    if (!response?.events || !Array.isArray(response.events)) {
      return [];
    }

    return response.events.map((row) => ({
      date: row[0] as string,
      count: row[1] as number,
    }));
  }

  private resolveFactuality(
    params: EventsContentParams,
    urlFactuality: string | undefined,
  ): EventFactuality[] | undefined {
    if (params.factuality) {
      return params.factuality;
    }
    if (urlFactuality) {
      return this.commonDataParams.getFactualityValues(
        urlFactuality,
      ) as EventFactuality[];
    }
    return undefined;
  }
}
